import Component from '@glimmer/component';
import { task } from 'ember-concurrency';
import { tracked } from 'tracked-built-ins';
import { service } from '@ember/service';
import { fn, hash, array } from '@ember/helper';
import { on } from '@ember/modifier';
import { t } from 'ember-intl';
import { not, and, eq, lt, or } from 'ember-truth-helpers';
import set from 'ember-set-helper/helpers/set';
import { HashicorpCloudResourcemanagerPolicyBindingMemberType } from '@clients/cloud-resource-manager';
// eslint-disable-next-line ember/no-at-ember-render-modifiers
import didInsert from '@ember/render-modifiers/modifiers/did-insert';
import {
  HdsFormTextInputField,
  HdsButton,
  HdsButtonSet,
  HdsSeparator,
  HdsTextDisplay,
  HdsApplicationState,
} from '@hashicorp/design-system-components/components';
import { Flex, WithErrors, FlexGrid } from 'core/components';
import { boxMargin, boxPadding } from 'core/modifiers';
import EditPermissions from 'manage-access/components/manage-access/form/edit-permissions';
import ChangeSummary from 'manage-access/components/manage-access/table/change-summary';
import RoleDefinitions from 'manage-access/components/manage-access/flyout/role-definitions';
import iamPolicySummaryChanges from 'manage-access/helpers/iam-policy-summary-changes';
import PolicyManager from 'manage-access/utils/policy-manager';
import { CREATE_TEMP_ID } from 'hcp/components/page/service-principals/project/create';

const baseKey = 'components.page.access-control.service-principals.form';

/**
 *
 * `ServicePrincipalFormV2Component` is a generic encapsulation of the HTML form
 * and client side validation logic used to create new service principals, as well as
 * being used to edit existing service principals.
 *
 * ```
 * <ServicePrincipal::FormV2
 *   @currentRolesLocation={{@currentRolesLocation}}
 *   @currentScopeLink={{@currentScopeLink}}
 *   @saveButtonText={{"Create"}}
 *   @servicePrincipal={{}}
 *   @projectRoles={{@projectRoles}}
 *   @policy={{@policy}}
 *   @isNameInputDisabled={{true}}
 *   @isSaveDisabled={{false}}
 *   @save={{async () => {}}}
 * />
 * ```
 *
 * @class ServicePrincipalFormComponent
 *
 */

export default class ServicePrincipalForm extends Component {
  /**
   * An object representing the current location of roles, with link and roles.
   * Required for the RoleDefinitionsFlyout.
   * @argument currentRolesLocation
   * @type {Object}
   * @required
   */

  /**
   * An object representing the current scope, with properties type, name, and id.
   * Required for the RoleDefinitionsFlyout.
   * @argument currentScopeLink
   * @type {Object}
   * @required
   */

  /**
   * Button text to use for "save" action.
   * @argument saveButtonText
   * @type {String}
   * @required
   */

  /**
   * Passed in async/generator Ember concurrency task with a perform callback
   * function to invoke upon form submission
   * NOTE: The form component just collects and validates input, doesn't call API
   * @argument save
   * @type {Function}
   * @required
   */

  /**
   * Optionally disables the service principal name form input
   * @argument isNameInputDisabled
   * @type {Boolean}
   */

  /**
   * Optionally disables the form submit button that would invoke the save action
   * @argument isSaveDisabled
   * @type {Boolean}
   */

  /**
   * Model data from IAM service for the service principal, used when editing
   * @argument servicePrincipal
   * @type {Object}
   */

  /**
   * Existing role associated with the service principal, used to pre-populate the dropdown menu when editing
   * @argument role
   * @type {String}
   */

  @service intl;

  @tracked editedPolicy;
  @tracked originalPolicy;
  @tracked servicePrincipalId =
    this.args?.servicePrincipal?.id || CREATE_TEMP_ID;
  @tracked servicePrincipalName = this.args.servicePrincipal?.name ?? '';
  @tracked showRoleDefinitionsFlyout = false;
  @tracked validationErrors = [];
  @tracked validationError;

  didInsert = () => {
    const { policy } = this.args;
    this.setEditedPolicy(policy);
  };

  setEditedPolicy(policy) {
    if (!policy) return;

    const DefaultPolicy = new PolicyManager({ policy });

    const member = {
      memberId: this.servicePrincipalId,
      memberType:
        HashicorpCloudResourcemanagerPolicyBindingMemberType.SERVICEPRINCIPAL,
    };
    const { roleIds = [] } = DefaultPolicy.getRolesByMember(member) ?? {};

    if (!roleIds.length) {
      DefaultPolicy.addMemberToRole(member, ['roles/viewer']);
    }

    // This originalPolicy is only edited once at did-insert in case we need
    // to add the default viewer role. It's used to be sent into the edit
    // permissions form as the initial policy.
    this.originalPolicy = DefaultPolicy.getRawPolicy();

    // The edited policy starts off in sync with the original policy and will be
    // updated onChange of the originalPolicy. The policy coming in from the
    // args will be used to calculate changes since it's the source of truth.
    this.editedPolicy = DefaultPolicy.getRawPolicy();
  }

  // Starts with a letter.
  nameStartsWithLetter = /^[a-z]+/i;

  // Ends with a letter or number.
  nameEndsWithAlphanumeric = /[a-z0-9]+$/i;

  // May only contain letters, numbers or hypens.
  nameInvalidCharacters = /[^a-z0-9-]/i;

  setName = (e) => {
    this.servicePrincipalName = e.target.value;
    this.validateForm();
  };

  onPolicyChange = ({ policy }) => {
    this.editedPolicy = policy;
  };

  save = task(async (evt) => {
    evt.preventDefault();

    if (!this.validateForm()) return;

    return await this.args.save.perform(
      this.editedPolicy,
      this.servicePrincipalName
    );
  });

  validateForm() {
    const fieldValidatorName = [
      {
        message: this.intl.t(`${baseKey}.errors.name.required`),
        isValid: (v) => v && v !== '',
        subValidators: [
          {
            message: this.intl.t(
              `${baseKey}.errors.name.must-start-with-ascii`
            ),
            isValid: (v) => this.nameStartsWithLetter.test(v),
          },
          {
            message: this.intl.t(
              `${baseKey}.errors.name.must-end-with-ascii-or-number`
            ),
            isValid: (v) => this.nameEndsWithAlphanumeric.test(v),
          },
          {
            message: this.intl.t(
              `${baseKey}.errors.name.must-only-contain-alphanumber-or-hyphen`
            ),
            isValid: (v) => !this.nameInvalidCharacters.test(v),
          },
          {
            message: this.intl.t(
              `${baseKey}.errors.name.must-be-between-specific-length`
            ),
            isValid: (v) => v.length >= 3 && v.length <= 36,
          },
        ],
      },
    ];

    let nameErrors = this.runValidators(
      fieldValidatorName,
      this.servicePrincipalName
    );
    this.validationErrors = nameErrors.map((v) => v.message);

    return !Object.keys(this.validationErrors).find(
      (k) => this.validationErrors[k].length
    );
  }

  runValidators(validators, value) {
    let failedValidators = validators.flatMap((v) => {
      if (!v.isValid(value)) {
        return v;
      }

      if (v.subValidators) {
        return this.runValidators(v.subValidators, value);
      }

      return [];
    });

    return failedValidators;
  }
  <template>
    <WithErrors @error={{this.save.last.error}} as |FormError FieldError|>
      <FormError />
      <div>
        <FlexGrid as |G|>
          <G.Item @sm="12" @md="12" @lg="12">
            <form
              data-test-form
              novalidate
              class="hcp-hds-form"
              {{on "submit" this.save.perform}}
              {{didInsert this.didInsert}}
            >
              <Flex @direction="column" @gap="xl">
                <section>
                  <Flex
                    @direction="column"
                    @gap="lg"
                    data-test-service-principal-form-v2
                  >
                    <FlexGrid as |G|>
                      <G.Item @sm="12" @md="12" @lg="8">
                        <FieldError
                          @field="name"
                          @hdsDelegatedError={{true}}
                          as |err|
                        >
                          <HdsFormTextInputField
                            @id="name"
                            @isRequired={{true}}
                            @value={{this.servicePrincipalName}}
                            disabled={{@isNameInputDisabled}}
                            name="name"
                            {{on "input" this.setName}}
                            {{on "blur" this.setName}}
                            data-test-service-principal-name
                            as |F|
                          >
                            <F.Label>
                              {{t
                                "components.page.access-control.service-principals.form.label.name"
                              }}
                            </F.Label>
                            {{#if err}}
                              <F.Error>
                                {{err.description}}
                              </F.Error>
                            {{else if this.validationErrors.length}}
                              <F.Error data-test-validation-name-error as |E|>
                                {{#each this.validationErrors as |m|}}
                                  <E.Message>
                                    {{m}}
                                  </E.Message>
                                {{/each}}
                              </F.Error>
                            {{/if}}
                          </HdsFormTextInputField>
                        </FieldError>
                      </G.Item>
                    </FlexGrid>
                    {{! for create, we have to send in a temporary id and update the member id when we create the service principal }}
                    <div>
                      <EditPermissions
                        @memberId={{this.servicePrincipalId}}
                        @memberType="SERVICE_PRINCIPAL"
                        @policy={{this.originalPolicy}}
                        @roles={{@projectRoles}}
                        @onChange={{this.onPolicyChange}}
                        {{boxPadding "0 0 md 0"}}
                      >
                        <:body as |B|>
                          <FlexGrid as |FG|>
                            <FG.Item @sm="12" @md="12" @lg="8">
                              <Flex
                                @alignItems="start"
                                @direction="row"
                                @gap="lg"
                                @grow={{1}}
                              >
                                <B.PermissionSelect
                                  @basicServiceLabel={{t
                                    "components.page.access-control.service-principals.form.edit-assignment.project-label"
                                    name=@project.name
                                  }}
                                />
                              </Flex>
                            </FG.Item>
                            <FG.Item @sm="12" @md="12" @lg="4">
                              <Flex
                                @alignItems="start"
                                @direction="row"
                                @gap="lg"
                                {{boxPadding "lg 0 0 0"}}
                              >
                                {{#if
                                  (not
                                    (and
                                      (eq B.data.permissions.size 1)
                                      (not B.data.permission.service)
                                    )
                                  )
                                }}
                                  <HdsButton
                                    @color="secondary"
                                    @icon="trash"
                                    @text={{B.removePermissionText}}
                                    {{on
                                      "click"
                                      (fn
                                        B.removePermission
                                        B.data.id
                                        B.data.permission
                                      )
                                    }}
                                    {{boxMargin "2xs 0 0 0"}}
                                    data-test-edit-permissions-remove-assignment-button
                                  />
                                {{/if}}
                                <HdsButton
                                  @color="tertiary"
                                  @icon="help"
                                  @iconPosition="leading"
                                  @isInline={{true}}
                                  @text={{t
                                    "components.page.access-control.service-principals.form.edit-assignment.view-role-definitions"
                                  }}
                                  {{on
                                    "click"
                                    (set this "showRoleDefinitionsFlyout" true)
                                  }}
                                  {{boxMargin "2xs 0 0 0"}}
                                  data-test-roles-chart-toggle-button
                                />
                              </Flex>
                            </FG.Item>
                          </FlexGrid>
                        </:body>
                        <:footer as |F|>
                          {{#if
                            (lt F.permissions.size F.currentServices.length)
                          }}
                            <HdsButtonSet>
                              <HdsButton
                                @color="tertiary"
                                @icon="plus"
                                @text={{F.addAssignmentText}}
                                {{on
                                  "click"
                                  (fn F.addPermission (hash service=""))
                                }}
                                data-test-edit-permissions-add-assignment-button
                              />
                            </HdsButtonSet>
                          {{/if}}
                        </:footer>
                      </EditPermissions>

                      <HdsSeparator />

                      <Flex
                        @direction="column"
                        @gap="lg"
                        @justifyContent="center"
                      >
                        <HdsTextDisplay @tag="h4" @size="300" @weight="bold">
                          {{if
                            this.servicePrincipalName.length
                            (t
                              "components.page.access-control.service-principals.form.edit-assignment.review-changes-for-service-principal-name"
                              servicePrincipalName=this.servicePrincipalName
                            )
                            (t
                              "components.page.access-control.service-principals.form.edit-assignment.review-changes"
                            )
                          }}
                        </HdsTextDisplay>
                        {{#let
                          (iamPolicySummaryChanges
                            @policy
                            this.editedPolicy
                            (hash memberIds=(array this.servicePrincipalId))
                          )
                          as |summary|
                        }}
                          {{#if summary.changes}}
                            <ChangeSummary
                              @changes={{summary.changes}}
                              @roles={{@projectRoles}}
                            />
                          {{else}}
                            <div
                              {{boxPadding "2xl 0"}}
                              data-test-manage-access-change-summary-application-state
                            >
                              <HdsApplicationState as |A|>
                                <A.Header
                                  @title={{t
                                    "components.page.access-control.service-principals.form.edit-assignment.no-changes-applied"
                                  }}
                                />
                                <A.Body
                                  @text={{t
                                    "components.page.access-control.service-principals.form.edit-assignment.select-a-role-to-see-changes"
                                  }}
                                />
                              </HdsApplicationState>
                            </div>
                          {{/if}}
                        {{/let}}
                      </Flex>
                    </div>
                  </Flex>
                </section>

                <footer>
                  <HdsButtonSet>
                    <HdsButton
                      data-test-submit
                      @text={{@saveButtonText}}
                      type="submit"
                      disabled={{or @isSaveDisabled this.save.isRunning}}
                    />
                    <HdsButton
                      @color="secondary"
                      @route="cloud.access-control.service-principals.list"
                      @text={{t "components.form.cancel"}}
                      data-test-cancel
                    />
                  </HdsButtonSet>
                </footer>
              </Flex>
            </form>
          </G.Item>
        </FlexGrid>
      </div>
      {{#if this.showRoleDefinitionsFlyout}}
        <RoleDefinitions
          @rolesLocation={{@currentRolesLocation}}
          @currentScopeLink={{@currentScopeLink}}
          @onClose={{set this "showRoleDefinitionsFlyout" false}}
        />
      {{/if}}
    </WithErrors>
  </template>
}
